import { action, computed, makeObservable, observable } from 'mobx';
import { getEnv } from '../../../helpers/mobx-easy-wrapper';
import {
  ClientUser,
  ConfigurableEmailType,
  PaymentProvider,
  RegisterRequest,
  SubscriptionStatusName,
  User,
  UserSubscription,
} from '@monorepo/types';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import {
  getFbcFromCookies,
  getFbpFromCookies,
  getShortsNinjaCookie,
} from '../../../helpers/cookies';
import { storageService } from '../../../services/storage-service';
import { INITIAL_URL_KEY } from '../../ui/ui-store';

export default class UserStore {
  @observable
  isLoggedIn = false;

  @observable
  currentUser: ClientUser = {} as ClientUser;

  @observable
  emailNotifications: Partial<Record<ConfigurableEmailType, boolean>> = {};

  @observable
  changelogCount = 0;

  constructor() {
    makeObservable(this);
  }

  @action
  setChangelogCount(count: number) {
    this.changelogCount = count;
  }

  async register(data: RegisterRequest) {
    try {
      const {
        apiFactory: { authService },
      } = getEnv();

      data.fbc = getFbcFromCookies();
      data.fbp = getFbpFromCookies();
      data.utm = getShortsNinjaCookie() || {};
      data.clickId = storageService.getClickId();

      const result = await authService.register(data);

      return result;
    } catch (e) {
      console.error(`Failed registering user`, e);
      throw e;
    }
  }

  async confirm(token: string) {
    try {
      const {
        apiFactory: { authService },
      } = getEnv();

      const user = await authService.confirm(token);

      this.setCurrentUser(user);
      this.setLoginState(true);
    } catch (e) {
      console.error(`Failed registering user`, e);
      throw e;
    }
  }

  async googleCallback(token: string) {
    try {
      const {
        apiFactory: { authService },
      } = getEnv();

      const fbc = getFbcFromCookies();
      const fbp = getFbpFromCookies();
      const utmData = getShortsNinjaCookie() || {};

      const user = await authService.googleCallback({
        token,
        fbc,
        fbp,
        utm: utmData,
        clickId: storageService.getClickId(),
      });

      this.setCurrentUser(user);
      this.setLoginState(true);
    } catch (e) {
      console.error(`Failed google auth`, e);
      throw e;
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<boolean> {
    try {
      const {
        apiFactory: { authService },
      } = getEnv();

      return await authService.resetPassword(token, newPassword);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async updateMetadata(): Promise<boolean> {
    try {
      const {
        apiFactory: { authService },
      } = getEnv();

      const fbc = getFbcFromCookies();
      const fbp = getFbpFromCookies();
      const utmData = getShortsNinjaCookie() || {};

      return await authService.updateMetadata({ fbc, fbp, utm: utmData });
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  async forgetPassword(email: string): Promise<boolean> {
    try {
      const {
        apiFactory: { authService },
      } = getEnv();

      return await authService.forgetPassword(email);
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async logout() {
    const {
      apiFactory: { authService },
    } = getEnv();

    await authService.logout();

    this.setCurrentUser({} as ClientUser);
    this.setLoginState(false);

    localStorage.removeItem(INITIAL_URL_KEY);
  }

  async login(email?: string, password?: string) {
    const storedAuth = localStorage.getItem('isAuthenticated');

    if (storedAuth !== 'true' && !email && !password) {
      return true;
    }

    try {
      const {
        apiFactory: { authService },
      } = getEnv();

      const fbc = getFbcFromCookies();
      const fbp = getFbpFromCookies();
      const utmData = getShortsNinjaCookie() || {};

      const user = await authService.login({
        email,
        password,
        fbc,
        fbp,
        utm: utmData,
      });

      if (user) {
        this.updateUserIfMetadata(user);
      }

      this.setCurrentUser(user);
      this.setLoginState(true);

      return user;
    } catch (e) {
      if ((e as AxiosError).status === 401) {
        localStorage.removeItem('isAuthenticated');
      }
      console.error(e);
      throw e;
    }
  }

  updateUserIfMetadata(user: ClientUser) {
    if (user.fbc && user.fbp && user.ip && user.userAgent && user.utm) {
      return;
    }

    this.updateMetadata();
  }

  async uploadAvatar(form: FormData) {
    const {
      apiFactory: { userService },
    } = getEnv();

    const user = await userService.uploadAvatar(form);

    this.setCurrentUser({ ...this.currentUser, ...user });
  }

  async updateDetails(userDto: Pick<User, 'firstName' | 'lastName' | 'email'>) {
    const {
      apiFactory: { userService },
    } = getEnv();

    const user = await userService.updateDetails(userDto);

    this.setCurrentUser({ ...this.currentUser, ...user });
  }

  async updatePassword(newPassword: string) {
    const {
      apiFactory: { userService },
    } = getEnv();

    return userService.updatePassword(newPassword);
  }

  async updateEmailNotifications(
    emailNotifications: Partial<Record<ConfigurableEmailType, boolean>>
  ) {
    const {
      apiFactory: { userService },
    } = getEnv();

    const updatedEmailNotifications =
      await userService.updateEmailNotifications(emailNotifications);

    // Only update the emailNotifications observable, not the entire user
    this.setEmailNotifications(updatedEmailNotifications);
  }

  async cancelSubscription() {
    const {
      apiFactory: { paymentsService },
    } = getEnv();

    return paymentsService.cancelSubscription();
  }

  async renewSubscription() {
    const {
      apiFactory: { paymentsService },
    } = getEnv();

    return paymentsService.renewSubscription();
  }

  async updatePaymentDetails() {
    const {
      apiFactory: { paymentsService },
    } = getEnv();

    return paymentsService.updatePaymentDetails();
  }

  async getCustomerPortal() {
    const {
      apiFactory: { paymentsService },
    } = getEnv();

    return paymentsService.getCustomerPortal();
  }

  async getPaymentIssueStatus() {
    const {
      apiFactory: { paymentsService },
    } = getEnv();

    return paymentsService.getPaymentIssueStatus();
  }

  async changePlan(planId: string) {
    const {
      apiFactory: { paymentsService },
    } = getEnv();

    return paymentsService.changePlan(planId);
  }

  async createCheckoutSession(planId: string) {
    const {
      apiFactory: { paymentsService },
    } = getEnv();

    return paymentsService.createCheckoutSession(planId);
  }

  async getLatestUser() {
    const {
      apiFactory: { userService },
    } = getEnv();

    const user = await userService.getAll();

    this.setCurrentUser({ ...this.currentUser, ...user });
  }

  async fetchFeaturebaseToken(): Promise<string> {
    const {
      apiFactory: { userService },
    } = getEnv();

    return userService.getFeaturebaseToken();
  }

  @action
  setCurrentUser(data: ClientUser) {
    this.currentUser = data;
    // Initialize emailNotifications from user data
    if (data.emailNotifications) {
      this.emailNotifications = data.emailNotifications;
    }
  }

  @action
  setEmailNotifications(
    notifications: Partial<Record<ConfigurableEmailType, boolean>>
  ) {
    this.emailNotifications = notifications;
  }

  @action
  setLoginState(loginState: boolean) {
    this.isLoggedIn = loginState;

    localStorage.setItem('isAuthenticated', loginState.toString());
  }

  @computed
  get fullName() {
    return `${this.currentUser.firstName.trim()} ${this.currentUser.lastName.trim()}`;
  }

  @computed
  get userSubscription(): UserSubscription {
    const defaultSubscription = {
      allowedSeriesAmount: 0,
      credits: 0,
    };

    return {
      ...defaultSubscription,
      ...(this.currentUser.subscription || {}),
    };
  }

  @computed
  get isSubscriptionActive() {
    const statusActive =
      this.userSubscription.status === SubscriptionStatusName.Active;
    const dateActive = this.userSubscription.nextChargeDate
      ? dayjs().isBefore(dayjs(this.userSubscription.nextChargeDate))
      : false;

    return statusActive || dateActive;
  }

  // PayPro suspended our account, so any subscription-management action that
  // would round-trip to PayPro (cancel, renew, change-plan, customer portal)
  // is dead. The billing page treats these users as if they had no
  // subscription so the only available action is re-subscribing via Polar.
  @computed
  get isPayproLocked() {
    return this.userSubscription.paymentProvider === PaymentProvider.PayPro;
  }

  @computed
  get canCreateVideos() {
    return (
      (this.userSubscription.credits || 0) +
        (this.userSubscription.topupCredits || 0) >
      0
    );
  }
}
